import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AuthenticatorSetup from './components/AuthenticatorSetup';
import SMSBackupSetup from './components/SMSBackupSetup';
import BackupCodesPanel from './components/BackupCodesPanel';
import SecurityEducation from './components/SecurityEducation';
import ProgressIndicator from './components/ProgressIndicator';

const TwoFactorAuthenticationSetup = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('method-selection');
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [authenticatorEnabled, setAuthenticatorEnabled] = useState(false);
  const [smsBackupEnabled, setSmsBackupEnabled] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [setupComplete, setSetupComplete] = useState(false);

  const steps = [
    { id: 'method-selection', label: 'Choose Method', icon: 'Shield' },
    { id: 'setup', label: 'Setup & Verify', icon: 'Settings' },
    { id: 'backup', label: 'Backup Codes', icon: 'Key' },
    { id: 'complete', label: 'Complete', icon: 'CheckCircle' }
  ];

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    setCurrentStep('setup');
  };

  const handleAuthenticatorComplete = (secret) => {
    setAuthenticatorEnabled(true);
    setCurrentStep('backup');
    generateBackupCodes();
  };

  const handleSMSComplete = () => {
    setSmsBackupEnabled(true);
    setCurrentStep('backup');
    generateBackupCodes();
  };

  const generateBackupCodes = () => {
    const codes = [];
    for (let i = 0; i < 10; i++) {
      const code = Math.random()?.toString(36)?.substring(2, 10)?.toUpperCase();
      codes?.push(code);
    }
    setBackupCodes(codes);
  };

  const handleFinishSetup = () => {
    setSetupComplete(true);
    setCurrentStep('complete');
  };

  const handleBackToDashboard = () => {
    const roleRoutes = {
      member: '/member-dashboard',
      trainer: '/trainer-dashboard',
      staff: '/staff-dashboard',
      manager: '/single-gym-owner-dashboard',
      owner: '/single-gym-owner-dashboard',
      sysadmin: '/system-administrator-dashboard'
    };
    navigate(roleRoutes?.[userProfile?.role] || '/member-dashboard');
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      
      <div className="page-content-wrapper">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="Shield" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Two-Factor Authentication</h1>
                <p className="text-muted-foreground">Strengthen your account security with 2FA</p>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <ProgressIndicator steps={steps} currentStep={currentStep} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Method Selection */}
              {currentStep === 'method-selection' && (
                <div className="card">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Choose Your 2FA Method</h2>
                  <p className="text-muted-foreground mb-6">Select how you want to secure your account</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Authenticator App */}
                    <button
                      onClick={() => handleMethodSelect('authenticator')}
                      className="p-6 rounded-xl border-2 border-border hover:border-primary transition-all text-left group"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name="Smartphone" size={24} className="text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-foreground mb-2">Authenticator App</h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            Use Google Authenticator, Authy, or Microsoft Authenticator
                          </p>
                          <div className="flex items-center gap-2">
                            <div className="px-2 py-1 rounded bg-success/10 text-success text-xs font-medium">
                              Most Secure
                            </div>
                            <div className="px-2 py-1 rounded bg-primary/10 text-primary text-xs font-medium">
                              Recommended
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* SMS Backup */}
                    <button
                      onClick={() => handleMethodSelect('sms')}
                      className="p-6 rounded-xl border-2 border-border hover:border-primary transition-all text-left group"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name="MessageSquare" size={24} className="text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-foreground mb-2">SMS Backup</h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            Receive verification codes via text message
                          </p>
                          <div className="flex items-center gap-2">
                            <div className="px-2 py-1 rounded bg-warning/10 text-warning text-xs font-medium">
                              Backup Option
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Setup Step */}
              {currentStep === 'setup' && selectedMethod === 'authenticator' && (
                <AuthenticatorSetup 
                  onComplete={handleAuthenticatorComplete}
                  onBack={() => setCurrentStep('method-selection')}
                />
              )}

              {currentStep === 'setup' && selectedMethod === 'sms' && (
                <SMSBackupSetup 
                  onComplete={handleSMSComplete}
                  onBack={() => setCurrentStep('method-selection')}
                />
              )}

              {/* Backup Codes Step */}
              {currentStep === 'backup' && (
                <BackupCodesPanel 
                  codes={backupCodes}
                  onContinue={handleFinishSetup}
                />
              )}

              {/* Complete Step */}
              {currentStep === 'complete' && (
                <div className="card text-center">
                  <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
                    <Icon name="CheckCircle" size={48} className="text-success" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-3">2FA Setup Complete!</h2>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Your account is now protected with two-factor authentication. You'll need to verify your identity when logging in.
                  </p>
                  
                  <div className="bg-muted/30 rounded-lg p-4 mb-6 max-w-md mx-auto">
                    <div className="flex items-start gap-3 text-left">
                      <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-muted-foreground">
                        <p className="font-medium text-foreground mb-1">What's Next?</p>
                        <ul className="space-y-1">
                          <li>• Keep your backup codes in a safe place</li>
                          <li>• Test your 2FA by logging out and back in</li>
                          <li>• You can manage 2FA settings in your profile</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <Button onClick={handleBackToDashboard} className="mx-auto">
                    Back to Dashboard
                  </Button>
                </div>
              )}
            </div>

            {/* Security Education Sidebar */}
            <div className="lg:col-span-1">
              <SecurityEducation />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuthenticationSetup;